import { cn } from '@/lib/cn'
import styles from '../pages/AnalyticsPage/AnalyticsPage.module.scss'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'

export interface KPIItem {
  icon: React.ReactNode
  label: string
  /** Raw count; ``null`` while loading. ``KPIGrid`` formats it. */
  value: number | null | undefined
  /** Per-cell hover hint surfaced via the native ``title`` attribute. */
  hint?: string | null
}

export interface KPIGridProps {
  items: KPIItem[]
  /** When true, every cell renders the skeleton shimmer regardless of value. */
  loading?: boolean
  /** Surfaces a discreet notice under the strip, whose cells show "—";
   *  falsy = no notice. */
  error?: unknown
  /** Refetch for the notice's "Tentar de novo". */
  onRetry?: () => void
}

/**
 * Platform-agnostic KPI strip (Inscritos / Views totais / Vídeos · etc).
 * Used by both ``YoutubeAnalyticsPage`` and ``TiktokAnalyticsPage`` —
 * each wires its own three counters from the relevant API.
 *
 * Style classes still live in ``AnalyticsPage.module.scss`` because
 * splitting SCSS across files is more churn than it's worth right now;
 * Phase 5 cleanup moves them when the TikTok page actually ships.
 */
export function KPIGrid({ items, loading = false, error, onRetry }: KPIGridProps) {
  const failed = Boolean(error)
  return (
    <>
      <div className={styles.kpiRow}>
        {items.map((item) => (
          <Kpi
            key={item.label}
            icon={item.icon}
            label={item.label}
            value={failed ? { display: '—' } : loading ? null : formatBigNumber(item.value)}
            hint={failed || loading ? null : item.hint ?? null}
          />
        ))}
      </div>
      {failed ? <QueryErrorNotice compact error={error} label="os números" onRetry={onRetry} /> : null}
    </>
  )
}

interface KpiProps {
  icon: React.ReactNode
  label: string
  value: { display: string; suffix?: string } | null
  hint: string | null
}

function Kpi({ icon, label, value, hint }: KpiProps) {
  return (
    <div className={styles.kpi} title={hint ?? ''}>
      <span className={styles.kpiLabel}>
        <span className={styles.kpiIcon} aria-hidden>{icon}</span>
        {label}
      </span>
      {value === null ? (
        <div className={cn(styles.skeletonShimmer, styles.skeletonValue)} aria-hidden />
      ) : (
        <span className={styles.kpiValue}>
          {value.display}
          {value.suffix ? <small>{value.suffix}</small> : null}
        </span>
      )}
    </div>
  )
}

export function formatBigNumber(raw: number | undefined | null): { display: string; suffix?: string } {
  const n = raw ?? 0
  if (!Number.isFinite(n) || n < 1000) return { display: String(n) }
  if (n >= 1_000_000) return { display: (n / 1_000_000).toFixed(1).replace(/\.0$/, ''), suffix: 'M' }
  if (n >= 1_000) return { display: (n / 1_000).toFixed(1).replace(/\.0$/, ''), suffix: 'k' }
  return { display: String(n) }
}
